import i18n from "../../../i18n";import { useState } from "react";
import { Eye, EyeOff, KeyRound } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { Button, ui, PageHeader, ContentCard, AlertBanner } from "../../../components/ui";
import { changePassword } from "../../../services/profileSettingsService";

// =============================================================================
// ChangePasswordPage.jsx — ESS -> Profile Settings -> Change Password.
//
// The module doesn't exist yet in the backend's enabledServices tree, so
// there's no ROUTE_LINKS entry driving this from the sidebar today — it's
// reached from the "Change Password" action on the My Profile page
// (see EmployeeInfoPage.jsx) and routed directly at /hrms-ess/change-password
// (see router/AppRoutes.jsx). Once the submodule is added to the login
// response, wire a ROUTE_LINKS key to this same route and the sidebar will
// pick it up automatically — no changes needed here.
//
// Whatever the backend sends back in `message` is shown as-is (success or
// error) — nothing here is hardcoded copy standing in for the API's own
// response text.
// =============================================================================

const FIELDS = [
{ key: "currentPassword", label: "Current password", autoComplete: "current-password" },
{ key: "newPassword", label: "New password", autoComplete: "new-password" },
{ key: "confirmPassword", label: "Confirm new password", autoComplete: "new-password" }];


const INITIAL_FORM = { currentPassword: "", newPassword: "", confirmPassword: "" };

export function ChangePasswordPage() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [visible, setVisible] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState(null); // { type, message }

  const updateField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const toggleVisible = (key) => {
    setVisible((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const validate = () => {
    if (!form.currentPassword || !form.newPassword || !form.confirmPassword) {
      return "All fields are required.";
    }
    if (form.newPassword !== form.confirmPassword) {
      return "New password and confirm password do not match.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBanner(null);

    const validationError = validate();
    if (validationError) {
      setBanner({ type: "error", message: validationError });
      return;
    }

    setSubmitting(true);
    try {
      const response = await changePassword(form);
      setBanner({ type: "success", message: response?.message || "Password changed successfully." });
      setForm(INITIAL_FORM);
      setVisible({});
    } catch (error) {
      setBanner({ type: "error", message: error?.message || "Failed to change password." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout formLayout="compact">
      <PageTitleRow back={<PageBackLink to="/" />}>
        <PageHeader title={i18n.t("formActions.changePassword")} />
      </PageTitleRow>
      

      <div className="w-full">
        <ContentCard>
          <form onSubmit={handleSubmit} className="flex flex-col gap-5 form-section">
            {banner &&
            <AlertBanner type={banner.type}>{banner.message}</AlertBanner>
            }

            {FIELDS.map(({ key, label, autoComplete }) =>
            <div key={key} className="flex flex-col gap-1.5">
                <label htmlFor={key} className={ui.sectionLabel}>
                  {label}
                </label>
                <div className="relative">
                  <input
                  id={key}
                  type={visible[key] ? "text" : "password"}
                  value={form[key]}
                  onChange={updateField(key)}
                  autoComplete={autoComplete}
                  placeholder="••••••••"
                  className={`${ui.input} w-full pr-10`} />
                
                  <button
                  type="button"
                  onClick={() => toggleVisible(key)}
                  aria-label={visible[key] ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
                  className="absolute right-3 top-1/2 -translate-y-1/2 per-file-hint hover:text-foreground transition">
                  
                    {visible[key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            )}

            <div className="form-actions">
            <Button type="submit" disabled={submitting} className={ui.primaryBtn}>
              <KeyRound className="h-4 w-4" />
              {submitting ? "Updating..." : "Update Password"}
            </Button>
            </div>
          </form>
        </ContentCard>
      </div>
    </AppLayout>);

}
